'use client';
import { Box, BoxProps, Container } from '@mui/material';
import { ReactNode, useMemo } from 'react';
import { useId } from 'react';

import {
  SearchBox,
  SearchBoxDataProps,
  SearchBoxPresentationProps,
  SearchResults,
  SearchResultsPropsData,
  SearchResultsPropsPresentation,
} from '@/components/Search';

interface SearchProps<T>
  extends Omit<BoxProps, 'results' | 'defaultValue' | 'title'>,
    SearchResultsPropsData<T>,
    Omit<SearchBoxDataProps, 'label'> {
  title?: string | ReactNode;
  wrapperProps?: BoxProps;
  searchBoxProps?: SearchBoxPresentationProps;
  searchResultsProps?: SearchResultsPropsPresentation;
  showResultsBeforeQuery?: boolean;
}

export function Search<T>({
  title = 'Search',
  defaultValue,
  onSearch,
  isSearching,
  results,
  renderResult,
  wrapperProps,
  searchBoxProps,
  searchResultsProps,
  indexProp,
  resultsAreaId = undefined,
  setPage,
  showResultsBeforeQuery,
}: SearchProps<T>) {
  const id = useId();

  const showResults = useMemo(() => {
    if (showResultsBeforeQuery) {
      return true;
    }
    return (defaultValue?.length ?? 0) > 0;
  }, [showResultsBeforeQuery, defaultValue]);

  return (
    <>
      <Box
        data-component="Search"
        sx={{
          textAlign: 'center',
          paddingInline: 2,
          paddingBlock: 6,
        }}
        {...wrapperProps}
      >
        <Container maxWidth="xl">
          <Container maxWidth="md">
            <SearchBox
              label={title}
              defaultValue={defaultValue}
              resultsAreaId={resultsAreaId ?? `search-results-${id}`}
              onSearch={onSearch}
              isSearching={isSearching}
              setPage={setPage}
              {...searchBoxProps}
            />
          </Container>
          {showResults && (
            <SearchResults
              renderResult={renderResult}
              results={results ?? []}
              indexProp={indexProp}
              isSearching={isSearching}
              {...searchResultsProps}
              wrapperProps={{
                ...searchResultsProps?.wrapperProps,
                id: resultsAreaId ?? `search-results-${id}`,
              }}
            />
          )}
        </Container>
      </Box>
    </>
  );
}
